import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { ExperienceCelebrationOverlay } from '../components/Gamification/ExperienceCelebrationOverlay'
import { ExperienceEngine, type ExperienceProcessResult } from '../services/ExperienceEngine'
import type { EngineRegistry, ExperienceReveal, PendingCelebration } from '../types/experienceEngine'
import { isNoSignInPreviewActive } from '../debug'
import { useAuth } from './useAuth'
import { useWorkspace } from './useWorkspace'
import { firebaseAuth } from '../services/firebase'
import { isFunPackEffectivelyEnabled } from '../services/funPackAccess'
import { subscribeToMyEffectiveControls } from '../services/memberControlsService'
import type { MemberControls } from '../types/memberControls'

export const ExperienceEngineProvider = ({ children }: PropsWithChildren) => {
  const { user, isAuthLoading } = useAuth()
  const { workspaces = [], activeWorkspaceId, activeRole } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null
  const [effectiveControls, setEffectiveControls] = useState<MemberControls | null>(null)
  const [prevWsId, setPrevWsId] = useState(activeWorkspaceId)
  if (prevWsId !== activeWorkspaceId) {
    setPrevWsId(activeWorkspaceId)
    setEffectiveControls(null)
  }

  useEffect(() => {
    if (!activeWorkspaceId) return undefined
    return subscribeToMyEffectiveControls(activeWorkspaceId, (controls) => {
      setEffectiveControls(controls)
    })
  }, [activeWorkspaceId])

  const funPackEnabled = isFunPackEffectivelyEnabled(
    activeWorkspace,
    { role: activeRole, effectiveControls },
    activeRole === 'OWNER',
  )

  const [reveal, setReveal] = useState<ExperienceReveal | null>(null)
  const [, setRegistry] = useState<EngineRegistry | null>(null)
  const [, setEnabled] = useState(false)
  const [, setPendingFeedbackForm] = useState<string | null>(null)

  const userUid = user?.uid

  const engine = useMemo(() => {
    if (!userUid) return null
    if (isAuthLoading) return null
    if (isNoSignInPreviewActive()) return null
    if (firebaseAuth.currentUser?.uid !== userUid) return null
    return new ExperienceEngine(userUid, {
      onRegistry: setRegistry,
      onEnabled: setEnabled,
      onReveal: setReveal,
      onProcessed: (results: ExperienceProcessResult[]) => {
        const feedbackForm = results.find((result) => result.feedbackFormId)?.feedbackFormId ?? null
        if (feedbackForm) setPendingFeedbackForm(feedbackForm)
      },
      onError: (error) => console.warn('[ExperienceEngine]', error),
    })
  }, [isAuthLoading, userUid])

  useEffect(() => {
    if (!engine) return undefined
    void engine.start()
    return () => engine.stop()
  }, [engine])

  const completeReveal = useCallback(async (items: PendingCelebration[]) => {
    setReveal(null)
    await engine?.acknowledge(items)
  }, [engine])

  return (
    <>
      {children}
      <ExperienceCelebrationOverlay reveal={funPackEnabled ? reveal : null} onComplete={completeReveal} />
    </>
  )
}
